import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();
    // DC 24/10/2026 : invisible tant qu'il n'est pas placé (sinon un instant visible en haut à gauche, en 0,0).
    this.style.animationName = 'none';
    this.style.opacity = '0';
    const intersectionObserver = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly | null} */
      let sourceRect = null;
      /** @type {DOMRectReadOnly | null} */
      let destinationRect = null;

      entries.forEach((entry) => {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      });

      if (sourceRect && destinationRect) {
        this.#animate(sourceRect, destinationRect);
      }

      intersectionObserver.disconnect();
    });
    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    // DC 24/10/2026 (saccades) : les animations CSS d'origine (animation-composition: accumulate sur translate,
    // border-radius dans les images-clés) ne pouvaient pas tourner sur le compositeur : l'image se figeait à chaque
    // tâche longue. On rejoue la MÊME trajectoire (mêmes courbes, même durée) avec element.animate() sur transform
    // et opacity uniquement. En cas de souci, on retombe sur les animations CSS d'origine.
    try {
      const cs = getComputedStyle(this);
      const curve = (name, fallback) => parseCubicBezier(cs.getPropertyValue(name)) || fallback;
      const ex = curve('--x-timing', [0.25, 0.1, 0.25, 1]);
      const ey = curve('--y-timing', [0.25, 0.1, 0.25, 1]);
      const es = curve('--scale-timing', [0.25, 0.1, 0.25, 1]);
      const durRaw = (cs.animationDuration || '0.6s').split(',')[0].trim();
      const duration = (parseFloat(durRaw) || 0.6) * (durRaw.endsWith('ms') ? 1 : 1000);
      const startOpacity = parseFloat(cs.getPropertyValue('--start-opacity'));
      const op0 = Number.isFinite(startOpacity) ? startOpacity : 1;
      const offY = parseFloat(cs.getPropertyValue('--offset-y')) || 10;
      const tx = endPoint.x - startPoint.x;
      const ty = endPoint.y - startPoint.y;
      const frames = [];
      const steps = 40;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const x = startPoint.x + tx * bezierAt(ex, t);
        const y = startPoint.y + ty * bezierAt(ey, t);
        const s = bezierAt(es, t);
        const scale = 1 - 0.75 * s;
        const opacity = t >= 0.05 ? 1 : op0 + (1 - op0) * (t / 0.05);
        frames.push({
          offset: t,
          opacity,
          // Reproduit le cumul (animation-composition: accumulate) de l'original : le translate(-50%, -50%) de base
          // s'additionne à celui de l'image-clé finale, d'où un décalage qui va de -50 % à -100 % au fil du vol.
          transform: `translate(${x}px, ${y}px) translate(${-50 - 50 * s}%, calc(${-50 - 50 * s}% + ${offY * s}px)) scale(${scale})`,
        });
      }
      this.style.animation = 'none';
      this.style.translate = 'none';
      const anim = this.animate(frames, { duration, easing: 'linear', fill: 'both' });
      await anim.finished.catch(() => {});
      this.remove();
      return;
    } catch (e) {
      this.style.removeProperty('animation');
      this.style.removeProperty('translate');
      this.style.removeProperty('opacity');
    }

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

/**
 * DC : lit « cubic-bezier(a, b, c, d) » dans une propriété CSS.
 * @param {string} v
 * @returns {number[] | null}
 */
function parseCubicBezier(v) {
  const m = String(v || '').match(/cubic-bezier\(\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*\)/);
  if (!m) return null;
  const p = m.slice(1).map(Number);
  return p.every(Number.isFinite) ? p : null;
}

/**
 * DC : valeur d'une courbe cubic-bezier CSS au temps t (0..1), comme le navigateur (résolution de x(u) = t).
 * @param {number[]} p - [x1, y1, x2, y2]
 * @param {number} t
 */
function bezierAt(p, t) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const [x1, y1, x2, y2] = p;
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sx = (u) => ((ax * u + bx) * u + cx) * u;
  const dx = (u) => (3 * ax * u + 2 * bx) * u + cx;
  let u = t;
  for (let i = 0; i < 8; i++) {
    const err = sx(u) - t;
    if (Math.abs(err) < 1e-6) break;
    const d = dx(u);
    if (Math.abs(d) < 1e-6) break;
    u -= err / d;
  }
  if (!(u >= 0 && u <= 1) || Math.abs(sx(u) - t) > 1e-4) {
    let lo = 0, hi = 1;
    u = t;
    for (let i = 0; i < 40; i++) {
      const xv = sx(u);
      if (Math.abs(xv - t) < 1e-6) break;
      if (xv < t) lo = u; else hi = u;
      u = (lo + hi) / 2;
    }
  }
  return ((ay * u + by) * u + cy) * u;
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
